import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2, Check, Plus, Eye } from 'lucide-react';
import api from '../../services/api';
import authService from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import AddGarageModal from '../garage/AddGarageModal';

interface Garage {
  id: number;
  name: string;
  address?: string;
  city?: string;
  is_active: boolean;
}

interface GarageSwitcherProps {
  onGarageChange?: (garageId: number | null) => void;
  allowMultiSelect?: boolean;
  selectedGarageIds?: number[];
  onMultiSelectChange?: (garageIds: number[]) => void;
}

const GarageSwitcher: React.FC<GarageSwitcherProps> = ({ 
  onGarageChange, 
  allowMultiSelect = false, 
  selectedGarageIds = [], 
  onMultiSelectChange 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [garages, setGarages] = useState<Garage[]>([]);
  const [currentGarageId, setCurrentGarageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGarages();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGarages = async () => {
    try {
      const response = await api.get('/garages/my-garages');
      setGarages(response.data.garages || []);
      setCurrentGarageId(response.data.current_garage_id || null);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch garages:', error);
      setLoading(false);
    }
  };

  const switchGarage = async (garageId: number | null) => {
    if (allowMultiSelect) {
      return; // Multi-select mode doesn't use switching
    }

    if (garageId === currentGarageId) {
      setIsOpen(false);
      return;
    }

    try {
      if (garageId === null) {
        // View all garages mode
        setCurrentGarageId(null);
        if (onGarageChange) onGarageChange(null);
        setIsOpen(false);
        return;
      }

      const response = await api.post('/garages/set-current', { garage_id: garageId });
      
      // Update tokens with new garage context
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      setCurrentGarageId(garageId);
      if (onGarageChange) onGarageChange(garageId);
      setIsOpen(false);

      // Refresh the page to reload data with new garage context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch garage:', error);
    }
  };

  const handleMultiSelectChange = (garageId: number, checked: boolean) => {
    if (!onMultiSelectChange) return;

    let newSelection = [...selectedGarageIds];
    if (checked) {
      if (!newSelection.includes(garageId)) {
        newSelection.push(garageId);
      }
    } else {
      newSelection = newSelection.filter(id => id !== garageId);
    }
    onMultiSelectChange(newSelection);
  };

  const handleSelectAll = () => {
    if (!onMultiSelectChange) return;
    
    if (selectedGarageIds.length === garages.length) {
      // Deselect all
      onMultiSelectChange([]);
    } else {
      // Select all
      onMultiSelectChange(garages.map(g => g.id));
    }
  };

  const handleAddGarage = () => {
    setIsOpen(false);
    setShowAddModal(true);
  };

  const handleGarageAdded = () => {
    fetchGarages();
  };

  const currentGarage = garages.find(g => g.id === currentGarageId);
  const user = authService.getCurrentUser();
  const isGarageOwner = user?.role === 'garage_owner';

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2">
        <Building2 className="w-5 h-5 text-gray-400" />
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  // Don't show switcher if user has only one garage
  if (garages.length <= 1 && !isGarageOwner) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#008B8B] flex-shrink-0" />
        <span className="text-xs sm:text-base text-gray-700 font-medium truncate max-w-[100px] sm:max-w-[150px] lg:max-w-none">
          {allowMultiSelect 
            ? `${selectedGarageIds.length} of ${garages.length} garages`
            : (currentGarage ? currentGarage.name : 'All Garages')
          }
        </span>
        <ChevronDown 
          className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              {allowMultiSelect ? 'Select Garages' : 'Switch Garage'}
            </p>
            {allowMultiSelect && garages.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-xs text-[#008B8B] hover:text-[#007A7A] mt-1"
              >
                {selectedGarageIds.length === garages.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {/* View All Option - Only in single select mode */}
          {!allowMultiSelect && garages.length > 1 && (
            <button
              onClick={() => switchGarage(null)}
              className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                currentGarageId === null ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex items-center space-x-3">
                <Eye className="w-5 h-5 text-[#008B8B]" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">All Garages</p>
                  <p className="text-sm text-gray-500">View aggregated data</p>
                </div>
              </div>
              {currentGarageId === null && (
                <Check className="w-5 h-5 text-[#008B8B]" />
              )}
            </button>
          )}

          <div className="border-t border-gray-200 my-2"></div>

          {/* Garage List */}
          <div className="max-h-64 overflow-y-auto">
            {garages.map((garage) => (
              <div
                key={garage.id}
                className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  allowMultiSelect
                    ? selectedGarageIds.includes(garage.id) ? 'bg-blue-50' : ''
                    : currentGarageId === garage.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  {allowMultiSelect ? (
                    <input
                      type="checkbox"
                      checked={selectedGarageIds.includes(garage.id)}
                      onChange={(e) => handleMultiSelectChange(garage.id, e.target.checked)}
                      className="w-4 h-4 text-[#008B8B] focus:ring-[#008B8B] border-gray-300 rounded"
                    />
                  ) : (
                    <Building2 className="w-5 h-5 text-gray-400" />
                  )}
                  <div 
                    className={`text-left ${allowMultiSelect ? '' : 'cursor-pointer'}`}
                    onClick={() => !allowMultiSelect && switchGarage(garage.id)}
                  >
                    <p className="font-medium text-gray-900">{garage.name}</p>
                    {garage.city && (
                      <p className="text-sm text-gray-500">{garage.city}</p>
                    )}
                  </div>
                </div>
                {!allowMultiSelect && currentGarageId === garage.id && (
                  <Check className="w-5 h-5 text-[#008B8B]" />
                )}
              </div>
            ))}
          </div>

          {/* Add New Garage Option for Garage Owners */}
          {isGarageOwner && (
            <>
              <div className="border-t border-gray-200 my-2"></div>
              <button
                onClick={handleAddGarage}
                className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <Plus className="w-5 h-5 text-[#008B8B]" />
                <span className="font-medium text-[#008B8B]">Add New Garage</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Add Garage Modal */}
      <AddGarageModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onGarageAdded={handleGarageAdded}
      />
    </div>
  );
};

export default GarageSwitcher;